// Texto de um motivo de trava (Parte 5.2 a–d): `authz:delegations.blocked.reasons.<código>` com a certificação, a tarefa, o conselho e a capacidade interpolados; um código
// que o front ainda não conhece cai no `detail` técnico do servidor (e, sem ele, no próprio código). O servidor manda `councilCode` OU `taskCode` na licença (nunca os dois):
// a mensagem escolhe a variante (`kind`) em vez de deixar um fragmento vazio.
import { useTranslation } from 'react-i18next';

import type { DelegationBlockReason } from '../../api/delegations';
import { isKnownBlockReason, skillCodesOf } from '../delegationModel';
import { useSkillName, useTaskName } from '../delegationNames';

export function useReasonText() {
  const { t } = useTranslation('authz');
  const taskName = useTaskName();
  const skillName = useSkillName();
  return (reason: DelegationBlockReason, taskNames?: ReadonlyMap<string, string>): string => {
    if (!isKnownBlockReason(reason.code)) {
      return reason.detail ?? reason.code;
    }
    const task = reason.taskCode ? taskName(reason.taskCode, taskNames?.get(reason.taskCode)) : '';
    const skills = reason.code === 'SKILL_MISSING' ? skillCodesOf(reason) : [];
    return t(`delegations.blocked.reasons.${reason.code}`, {
      defaultValue: reason.detail ?? reason.code,
      certification: reason.certificationTypeCode ?? '',
      task,
      council: reason.councilCode ?? '',
      skill: skills.map((code) => skillName(code)).join(', '),
      kind: kindOf(reason, skills),
    });
  };
}

/** A variante da mensagem: o conselho ou a tarefa da licença; as capacidades que faltam; `none` quando o motivo não traz o dado. */
function kindOf(reason: DelegationBlockReason, skills: readonly string[]): string {
  if (reason.code === 'LICENSE_MISSING') {
    return reason.councilCode ? 'council' : reason.taskCode ? 'task' : 'none';
  }
  if (reason.code === 'SKILL_MISSING') {
    return skills.length > 0 ? 'skill' : 'none';
  }
  return 'none';
}
